import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  FiBookOpen,
  FiCheck,
  FiFilter,
  FiMonitor,
  FiSearch,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import Breadcrumbs from '../../../Components/Breadcrumbs/BreadcrumbsComponent.component';
import { tags } from '../SearchDetails/SearchBar/Tags';

import {
  HeroSection,
  HeroContainer,
  HeroTop,
  HeroContent,
  HeroEyebrow,
  HeroTitle,
  HeroDescription,
  TrainingPanel,
  TrainingPanelHeader,
  TrainingPanelIcon,
  TrainingPanelEyebrow,
  TrainingPanelTitle,
  TrainingPanelDescription,
  TrainingOptions,
  TrainingOption,
  TrainingOptionIcon,
  TrainingOptionText,
  SearchArea,
  SearchToolbar,
  SearchBox,
  SearchIcon,
  SearchInput,
  ClearSearchButton,
  FilterWrap,
  FilterButton,
  FilterCount,
  FilterPopover,
  PopoverHeader,
  PopoverCloseButton,
  TagSearchInput,
  TagList,
  TagOption,
  TagOptionCheck,
  PopoverFooter,
  ClearButton,
  ActiveFilterRow,
  ActiveFilterLabel,
  ActiveChip,
  PartTwoNote,
  PartTwoBadge,
  PartTwoText,
} from './CourseCatalogHero.styles';

const CourseCatalogHero = ({
  searchTerm,
  setSearchTerm,
  selectedTag,
  setSelectedTag,
  // optional language filter
  selectedLanguage,
  setSelectedLanguage,
  languages,
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [tagSearch, setTagSearch] = useState('');

  const activeTag = tags.find((tag) => tag.id === selectedTag);

  const filteredTags = tags.filter((tag) =>
    tag.label.toLowerCase().includes(tagSearch.toLowerCase())
  );

  const normalize = (s) => String(s || '').toLowerCase();

  const handleTagSelect = (tagId) => {
    setSelectedTag(tagId);
    setIsFilterOpen(false);
    setTagSearch('');
  };

  const clearTag = () => {
    setSelectedTag('all');
    setTagSearch('');
  };

  const handleFilterToggle = () => {
    setIsFilterOpen((previous) => !previous);
  };

  return (
    <HeroSection>
      <Breadcrumbs
        one="Home"
        oneLink="/"
        two="Online Training"
        twoLink="/training"
      />
      <HeroContainer>
        <HeroTop>
          <HeroContent>
            <HeroEyebrow>Online Safety Training & Courses</HeroEyebrow>

            <HeroTitle>
              Find the Right Training
              <br />
              for Your Team
            </HeroTitle>

            <HeroDescription>
              Explore online workplace safety courses designed to make training
              accessible and convenient. Complete safety training online at your
              own pace, access your certificates, and choose from courses
              covering WHMIS, TDG, Fall Protection and more.
            </HeroDescription>
          </HeroContent>

          <TrainingPanel>
            <TrainingPanelHeader>
              <TrainingPanelIcon aria-hidden="true">
                <FiBookOpen />
              </TrainingPanelIcon>

              <div>
                <TrainingPanelEyebrow>Training Options</TrainingPanelEyebrow>

                <TrainingPanelTitle>
                  Flexible Learning.
                  <br />
                  Practical Support.
                </TrainingPanelTitle>
              </div>
            </TrainingPanelHeader>

            <TrainingPanelDescription>
              Browse training options for individual workers, teams, and
              workplace requirements.
            </TrainingPanelDescription>

            <TrainingOptions>
              <TrainingOption>
                <TrainingOptionIcon aria-hidden="true">
                  <FiMonitor />
                </TrainingOptionIcon>

                <TrainingOptionText>
                  <strong>Online Courses</strong>
                  <span>Flexible course access</span>
                </TrainingOptionText>
              </TrainingOption>

              <TrainingOption>
                <TrainingOptionIcon aria-hidden="true">
                  <FiCheck />
                </TrainingOptionIcon>

                <TrainingOptionText>
                  <strong>Certifications</strong>
                  <span>Recognized training options</span>
                </TrainingOptionText>
              </TrainingOption>

              <TrainingOption>
                <TrainingOptionIcon aria-hidden="true">
                  <FiBookOpen />
                </TrainingOptionIcon>

                <TrainingOptionText>
                  <strong>Two-Part Training</strong>
                  <span>Online and practical learning</span>
                </TrainingOptionText>
              </TrainingOption>

              <TrainingOption>
                <TrainingOptionIcon aria-hidden="true">
                  <FiUsers />
                </TrainingOptionIcon>

                <TrainingOptionText>
                  <strong>Team Training</strong>
                  <span>Courses for workplace teams</span>
                </TrainingOptionText>
              </TrainingOption>
            </TrainingOptions>
          </TrainingPanel>
        </HeroTop>

        <SearchArea>
          <SearchToolbar>
            <SearchBox>
              <SearchIcon aria-hidden="true">
                <FiSearch />
              </SearchIcon>

              <SearchInput
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search by course name, topic, or keyword..."
                aria-label="Search courses"
              />

              {searchTerm && (
                <ClearSearchButton
                  type="button"
                  onClick={() => setSearchTerm('')}
                  aria-label="Clear course search"
                >
                  <FiX />
                </ClearSearchButton>
              )}
            </SearchBox>

            <FilterWrap>
              <FilterButton
                type="button"
                onClick={handleFilterToggle}
                $active={selectedTag !== 'all' || isFilterOpen}
                aria-expanded={isFilterOpen}
                aria-controls="course-filter-popover"
              >
                <FiFilter />

                <span>Filters</span>

                {selectedTag !== 'all' && <FilterCount>1</FilterCount>}
              </FilterButton>

              {isFilterOpen && (
                <FilterPopover
                  id="course-filter-popover"
                  role="dialog"
                  aria-label="Course filters"
                >
                  <PopoverHeader>
                    <div>
                      {/* <span>Course Filters</span> */}
                      <h3>Filter by topic</h3>
                    </div>

                    <PopoverCloseButton
                      type="button"
                      onClick={() => setIsFilterOpen(false)}
                      aria-label="Close course filters"
                    >
                      <FiX />
                    </PopoverCloseButton>
                  </PopoverHeader>

                  <TagSearchInput
                    type="search"
                    value={tagSearch}
                    onChange={(event) => setTagSearch(event.target.value)}
                    placeholder="Search filters..."
                    aria-label="Search course filters"
                  />

                  <TagList>
                    {filteredTags.length > 0 ? (
                      filteredTags.map((tag) => (
                        <li key={tag.id}>
                          <TagOption
                            type="button"
                            onClick={() => handleTagSelect(tag.id)}
                            $active={selectedTag === tag.id}
                          >
                            <span>{tag.label}</span>

                            {selectedTag === tag.id && (
                              <TagOptionCheck aria-hidden="true">
                                <FiCheck />
                              </TagOptionCheck>
                            )}
                          </TagOption>
                        </li>
                      ))
                    ) : (
                      <li>
                        <TagOption as="div">
                          <span>No matching filters found.</span>
                        </TagOption>
                      </li>
                    )}
                  </TagList>

                  {/* Language filter */}
                  <div style={{ marginTop: 'var(--space-m)' }}>
                    <PopoverHeader>
                      <div>
                        <h3>Filter by language</h3>
                      </div>
                    </PopoverHeader>

                    <div
                      style={{
                        display: 'flex',
                        gap: 8,
                        flexWrap: 'wrap',
                        marginTop: 8,
                      }}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedLanguage && setSelectedLanguage('all')
                        }
                        style={{
                          padding: '0.45rem 0.7rem',
                          borderRadius: 8,
                          border: '1px solid var(--grey)',
                          background:
                            !selectedLanguage ||
                            normalize(selectedLanguage) === 'all'
                              ? 'var(--yellow1)'
                              : 'transparent',
                          color:
                            !selectedLanguage ||
                            normalize(selectedLanguage) === 'all'
                              ? 'var(--grey7)'
                              : 'var(--grey6)',
                          fontWeight: 800,
                          cursor: 'pointer',
                        }}
                      >
                        All
                      </button>

                      {(
                        languages || [
                          'English',
                          'French',
                          'Mandarin',
                          'Punjabi',
                          'Cantonese',
                          'Spanish',
                          'Arabic',
                          'Tagalog',
                          'Italian',
                          'German',
                          'Portuguese',
                          'Urdu',
                          'Russian',
                          'Hindi',
                          'Vietnamese',
                          'Korean',
                          'Persian (Farsi)',
                          'Tamil',
                          'Gujarati',
                          'Polish',
                        ]
                      ).map((lang) => (
                        <button
                          key={normalize(lang)}
                          type="button"
                          onClick={() =>
                            setSelectedLanguage && setSelectedLanguage(lang)
                          }
                          style={{
                            padding: '0.45rem 0.7rem',
                            borderRadius: 8,
                            border: '1px solid var(--grey)',
                            background:
                              normalize(selectedLanguage) === normalize(lang)
                                ? 'var(--yellow1)'
                                : 'transparent',
                            color:
                              normalize(selectedLanguage) === normalize(lang)
                                ? 'var(--grey7)'
                                : 'var(--grey6)',
                            fontWeight: 800,
                            cursor: 'pointer',
                          }}
                        >
                          {lang}
                        </button>
                      ))}
                    </div>
                  </div>

                  <PopoverFooter>
                    <ClearButton
                      type="button"
                      onClick={clearTag}
                      disabled={selectedTag === 'all'}
                    >
                      Clear filter
                    </ClearButton>
                  </PopoverFooter>
                </FilterPopover>
              )}
            </FilterWrap>
          </SearchToolbar>

          {selectedTag !== 'all' && activeTag && (
            <ActiveFilterRow>
              <ActiveFilterLabel>Filtered by</ActiveFilterLabel>

              <ActiveChip type="button" onClick={clearTag}>
                <span>{activeTag.label}</span>
                <FiX />
              </ActiveChip>
            </ActiveFilterRow>
          )}

          {selectedLanguage && selectedLanguage !== 'all' && (
            <ActiveFilterRow>
              <ActiveFilterLabel>Language</ActiveFilterLabel>

              <ActiveChip
                type="button"
                onClick={() =>
                  setSelectedLanguage && setSelectedLanguage('all')
                }
              >
                <span>{selectedLanguage}</span>
                <FiX />
              </ActiveChip>
            </ActiveFilterRow>
          )}

          <PartTwoNote>
            <PartTwoBadge>Part 2</PartTwoBadge>

            <PartTwoText>
              Courses marked with a Part 2 badge include a required in-person
              practical component in Nova Scotia.
            </PartTwoText>
          </PartTwoNote>
        </SearchArea>
      </HeroContainer>
    </HeroSection>
  );
};

CourseCatalogHero.propTypes = {
  searchTerm: PropTypes.string.isRequired,
  setSearchTerm: PropTypes.func.isRequired,
  selectedTag: PropTypes.string.isRequired,
  setSelectedTag: PropTypes.func.isRequired,
  selectedLanguage: PropTypes.string,
  setSelectedLanguage: PropTypes.func,
  languages: PropTypes.array,
};

export default CourseCatalogHero;
